import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  MultiSelect,
  Stack,
  Alert,
  Loader,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAddBidCommitteeMembers } from '../hooks/useEvaluationReports';
import { userApi } from '../../../api/users';

interface AddBidCommitteeMembersModalProps {
  opened: boolean;
  onClose: () => void;
  reportId: string;
  onSuccess?: () => void;
}

export function AddBidCommitteeMembersModal({
  opened,
  onClose,
  reportId,
  onSuccess,
}: AddBidCommitteeMembersModalProps) {
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [bidCommitteeMembers, setBidCommitteeMembers] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const addMembers = useAddBidCommitteeMembers();

  useEffect(() => {
    if (opened) {
      loadUsers();
    }
  }, [opened]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const users = await userApi.getUsersForEvaluationReport();
      const members = users
        .filter((u) => u.role === 'BID_COMMITTEE_MEMBER')
        .map((u) => ({ value: u.id, label: u.fullName }));

      setBidCommitteeMembers(members);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAdd = () => {
    if (memberIds.length === 0) {
      return;
    }

    addMembers.mutate(
      {
        reportId,
        data: {
          memberIds,
        },
      },
      {
        onSuccess: () => {
          onClose();
          setMemberIds([]);
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Bid Committee Members"
      size="md"
    >
      <Stack gap="md">
        {loadingUsers ? (
          <Loader size="md" />
        ) : (
          <>
            {bidCommitteeMembers.length === 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                No bid committee members found. Please add users with BID_COMMITTEE_MEMBER role.
              </Alert>
            )}

            <MultiSelect
              label="Bid Committee Members"
              placeholder="Select bid committee members"
              data={bidCommitteeMembers}
              value={memberIds}
              onChange={setMemberIds}
              required
              searchable
            />

            <Button
              onClick={handleAdd}
              loading={addMembers.isPending}
              disabled={memberIds.length === 0 || bidCommitteeMembers.length === 0}
              fullWidth
            >
              Add Members
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}

